'use client';

import * as React from 'react';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { TableResizeHandle } from '@/components/table-resize-handle';
import { TableSortButton } from '@/components/table-sort-button';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  ChevronDown,
  Search,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  Bell,
  Mail,
  Plus
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Campaign, CampaignStatus, CampaignChannel } from './types';
import { Card, CardContent, CardTitle } from '@/components/ui/card';
import { TableSkeleton } from '@/components/ui/skeletons';
import { FilterSystem, FilterOption } from '@/components/filter-system';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DataTableProps {
  data: Campaign[];
  columns: ColumnDef<Campaign>[];
  isLoading?: boolean;
  error?: {
    message: string;
    retry: () => void;
  };
  onCreateClick?: () => void;
  onRowClick?: (campaign: Campaign) => void;
}

export function DataTable({
  data,
  columns,
  isLoading,
  error,
  onCreateClick,
  onRowClick
}: DataTableProps) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [globalFilter, setGlobalFilter] = React.useState('');
  const [selectedStatuses, setSelectedStatuses] = React.useState<CampaignStatus[]>([]);
  const [selectedChannels, setSelectedChannels] = React.useState<CampaignChannel[]>([]);
  const [columnSizing, setColumnSizing] = React.useState({});

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      globalFilter,
      columnSizing,
      columnPinning: {
        right: ['actions']
      }
    },
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
    defaultColumn: {
      minSize: 50,
      maxSize: 500,
      size: 150
    },
    initialState: {
      pagination: {
        pageSize: 6
      }
    },
    filterFns: {
      status: (row, columnId, filterValue: CampaignStatus[]) => {
        if (!filterValue.length) return true;
        return filterValue.includes(row.getValue<CampaignStatus>('status'));
      },
      channels: (row, columnId, filterValue: CampaignChannel[]) => {
        if (!filterValue.length) return true;
        const rowChannels = row.getValue<CampaignChannel[]>('channels');
        return filterValue.some((channel) => rowChannels.includes(channel));
      }
    }
  });

  // Update filters when selections change
  React.useEffect(() => {
    if (selectedStatuses.length) {
      table.getColumn('status')?.setFilterValue(selectedStatuses);
    } else {
      table.getColumn('status')?.setFilterValue(undefined);
    }
  }, [selectedStatuses, table]);

  React.useEffect(() => {
    if (selectedChannels.length) {
      table.getColumn('channels')?.setFilterValue(selectedChannels);
    } else {
      table.getColumn('channels')?.setFilterValue(undefined);
    }
  }, [selectedChannels, table]);

  const statusOptions: FilterOption<CampaignStatus>[] = [
    { value: 'draft', label: 'Draft' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'sending', label: 'Sending' },
    { value: 'paused', label: 'Paused' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'suspended', label: 'Suspended' }
  ];

  const channelOptions: FilterOption<CampaignChannel>[] = [
    { value: 'sms', label: 'SMS', icon: <MessageSquare className="h-4 w-4" /> },
    { value: 'email', label: 'Email', icon: <Mail className="h-4 w-4" /> },
    { value: 'push', label: 'Push', icon: <Bell className="h-4 w-4" /> }
  ];

  return (
    <Card className="border-none bg-card">
      <CardContent className="p-8">
        <CardTitle className="text-xl font-normal mb-6">Campaigns</CardTitle>
        <div className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1">
              <div className="relative sm:w-[320px] w-full">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/70" />
                <Input
                  placeholder="Filter campaigns..."
                  value={globalFilter}
                  onChange={(event) => {
                    setGlobalFilter(event.target.value);
                  }}
                  className="pl-9 bg-background border-border/40 focus-visible:ring-1 focus-visible:ring-border"
                  disabled={isLoading ?? !!error}
                />
              </div>

              <FilterSystem<CampaignStatus>
                type="Status"
                options={statusOptions}
                selected={selectedStatuses}
                onChange={setSelectedStatuses}
              />

              <FilterSystem<CampaignChannel>
                type="Channels"
                options={channelOptions}
                selected={selectedChannels}
                onChange={setSelectedChannels}
              />
            </div>

            <div className="flex gap-2 shrink-0">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-9 border-border/30 bg-background hover:bg-background hover:border-border/60"
                    disabled={!!error}
                  >
                    Columns <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {table
                    .getAllColumns()
                    .filter((column) => column.getCanHide())
                    .map((column) => (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className="capitalize text-sm"
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) => {
                          column.toggleVisibility(value);
                        }}
                      >
                        {column.id}
                      </DropdownMenuCheckboxItem>
                    ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                onClick={onCreateClick}
                className="h-9 bg-primary hover:bg-primary/90"
                disabled={!!error}
              >
                <Plus className="mr-2 h-4 w-4" />
                Create Campaign
              </Button>
            </div>
          </div>

          <div className="rounded-md border border-border/40 bg-card">
            <div className="relative w-full overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-border/40">
                    {table.getHeaderGroups().map((headerGroup) =>
                      headerGroup.headers.map((header) => (
                        <TableHead
                          key={header.id}
                          className={cn(
                            'group h-12 text-sm font-medium text-muted-foreground bg-transparent relative',
                            header.column.id === 'actions' &&
                              'sticky right-0 bg-card group-hover:bg-accent/80 transition-colors duration-200'
                          )}
                          style={{ width: header.getSize() }}
                        >
                          {header.isPlaceholder ? null : (
                            <TableSortButton column={header.column}>
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </TableSortButton>
                          )}
                          {header.column.getCanResize() && (
                            <TableResizeHandle
                              onMouseDown={header.getResizeHandler()}
                              onTouchStart={header.getResizeHandler()}
                            />
                          )}
                        </TableHead>
                      ))
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableSkeleton rowCount={6} columnCount={columns.length - 1} showIcon={false} />
                  ) : error ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} className="h-32 p-0">
                        <div className="flex flex-col items-center justify-center h-full space-y-4 px-4 py-8 text-center bg-destructive/5">
                          <AlertCircle className="h-6 w-6 text-destructive" />
                          <div className="space-y-2">
                            <h3 className="font-semibold text-foreground">
                              Failed to load campaigns
                            </h3>
                            <p className="text-sm text-muted-foreground max-w-[400px]">
                              {error.message}
                            </p>
                          </div>
                          <Button variant="outline" onClick={error.retry} className="mt-2">
                            Try again
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : table.getRowModel().rows.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        className="border-border/40 hover:bg-accent/20 transition-colors duration-200 cursor-pointer group"
                        onClick={() => onRowClick?.(row.original)}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            key={cell.id}
                            className={cn(
                              'text-sm group-hover:text-foreground/90',
                              cell.column.id === 'actions' &&
                                'sticky right-0 bg-card group-hover:bg-accent transition-colors duration-200'
                            )}
                            style={{ width: cell.column.getSize() }}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={columns.length} className="h-24 text-center">
                        No campaigns found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="flex items-center justify-between py-4">
            <div className="text-sm text-muted-foreground">
              Showing {table.getRowModel().rows.length} of {data.length} campaigns
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    table.previousPage();
                  }}
                  disabled={!table.getCanPreviousPage() || !!error}
                  className="h-8 w-8 p-0 border-border/40 hover:bg-accent/[0.02] hover:border-border/60"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="text-sm text-muted-foreground">
                  Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    table.nextPage();
                  }}
                  disabled={!table.getCanNextPage() || !!error}
                  className="h-8 w-8 p-0 border-border/40 hover:bg-accent/[0.02] hover:border-border/60"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
